import { ProjectSettings } from 'shared/types/ProjectSettings';
import { mocksitePhotoSourceUrl } from 'components/commercetools-ui/organisms/product/product-list/context/constants';
import scaledWidthHeight from 'helpers/builderDimension';
import { roundToSix } from 'helpers/utils/util';

export type ImageOptions = {
  maxSize?: number,
};

export type OriginalPhotoInfo = {
  type: string;
  id: string;
  width: number;
  height: number;
  url: string;
  contentRegion?: ContentRegion;
  date?: string;
  expirationDate: string; // Set by our middleware
};

export type TransformedPhotoInfo = {
  height: number;
  id: string;
  metadata: {
    contentRegion?: ContentRegion;
  };
  mimeType: string;
  type: 'photo';
  urlPrint: string;
  urlThumb: string;
  versions: { height: number; url: string; width: number }[];
  width: number;
  expirationDate: string;
};

type ContentRegion = { height: number; width: number; x: number; y: number };
type UrlBuilder = (photo: OriginalPhotoInfo, height: number, width: number) => string;

const clampSize = (size: number, options: ImageOptions): number => {
  return Math.min(size, options.maxSize ?? Infinity);
};

const getContentRegion = (contentRegion: ContentRegion, scale: number): ContentRegion => {
  return {
    x: roundToSix(contentRegion.x * scale),
    y: roundToSix(contentRegion.y * scale),
    width: roundToSix(contentRegion.width * scale),
    height: roundToSix(contentRegion.height * scale),
  };
};

const appendOrSetOutputSize = (url: string, size: number): string => {
  const parsedUrl = new URL(url);
  parsedUrl.searchParams.set('outputsize', String(size));
  return parsedUrl.toString();
};

const urlBuilders: Record<string, UrlBuilder> = {
  mocksitephotos: (photo, height, width) => appendOrSetOutputSize(photo.url, Math.max(height, width)),
  googlephotos: (photo, height, width) => `${photo.url}=w${width}-h${height}-c`
};

const getPhotoSource = (photoUrl: string): string => {
  return mocksitePhotoSourceUrl.some((base) => photoUrl.startsWith(base))
    ? 'mocksitephotos'
    : 'googlephotos';
};

export const isGooglePhotosUrl = (url: string): boolean => getPhotoSource(url) === 'googlephotos';

/**
 * Cache-busts a retried image request.
 *
 * Google Photos (lh3) serving URLs carry their options in a trailing `=w..-h..-c` segment and
 * reject anything appended after it with a 400 — so a retried Google URL has to stay byte
 * identical, and the `<img>` remount is what re-issues the request. Only URLs that are already
 * query-driven (mocksite) can take a cache-busting param.
 */
export const buildRetrySrc = (src: string, attempts: number): string => {
  if (attempts === 0 || isGooglePhotosUrl(src)) return src;
  return `${src}${src.includes('?') ? '&' : '?'}retry=${attempts}`;
};

export const setupImageOptions = (projectSettings?: ProjectSettings): ImageOptions => {
  return {
    maxSize: projectSettings?.MAX_PHOTO_FETCH_SIZE,
  };
};

export const getBuiltUrl = (file: OriginalPhotoInfo, options: ImageOptions, size: number): string => {
  const builder = urlBuilders[getPhotoSource(file.url)];
  const clampedSize = clampSize(size, options);
  const { scaledWidth: width, scaledHeight: height } = scaledWidthHeight(file.width, file.height, clampedSize);
  return builder(file, height, width);
};

export const getImageVersions = (photo: OriginalPhotoInfo, sizes: number[], options: ImageOptions) => {
  const builder = urlBuilders[getPhotoSource(photo.url)];
  return sizes.map((size) => {
    const clampedSize = clampSize(size, options);
    const { scaledWidth, scaledHeight } = scaledWidthHeight(photo.width, photo.height, clampedSize);

    return {
      width: scaledWidth,
      height: scaledHeight,
      url: builder(photo, scaledHeight, scaledWidth)
    };
  });
};

export const convertGoogleImageData = (file: OriginalPhotoInfo, options: ImageOptions): TransformedPhotoInfo => {
  const urlBuilder = urlBuilders[getPhotoSource(file.url)];

  // Compute clamped full size
  const originalSize = Math.max(file.width, file.height);
  const size = clampSize(originalSize, options);
  const scale = size / originalSize;
  
  const { scaledWidth: width, scaledHeight: height } = scaledWidthHeight(file.width, file.height, size);
  const { scaledWidth: thumbnailWidth, scaledHeight: thumbnailHeight } = scaledWidthHeight(file.width, file.height, clampSize(200, options));

  return {
    type: 'photo',
    mimeType: file.type,
    id: file.id,
    width,
    height,
    urlThumb: urlBuilder(file, thumbnailHeight, thumbnailWidth),
    urlPrint: urlBuilder(file, height, width ),
    versions: getImageVersions(file, [200, 700, size], options),
    metadata: {
      ...(file.contentRegion && { contentRegion: getContentRegion(file.contentRegion, scale) }),
    },
    expirationDate: file.expirationDate,
  };
};
